/**
 * Bundle Builder Product Card
 *
 * Handles interactions for bundle builder product cards including:
 * - Color swatch changes (image updates, variant selection)
 * - Add to bag functionality
 * - Quantity selector controls (+, -, direct input)
 * - Custom event communication with bundle builder
 *
 * Uses event delegation to avoid duplicate handlers.
 */

;(function () {
  'use strict'

  let initialized = false

  /**
   * Initialize bundle product card event delegation
   * Only runs once - uses event delegation so new cards work automatically
   */
  function initBundleProductCards() {
    if (initialized) return
    initialized = true

    // Event delegation for all bundle card interactions
    document.addEventListener('click', handleBundleCardClick)
    document.addEventListener('change', handleBundleCardChange)
    document.addEventListener('keydown', handleBundleSwatchKeyboard)
  }

  window.initBundleProductCards = initBundleProductCards

  /**
   * Handle keyboard navigation for color swatches in bundle cards.
   * Implements roving tabindex: Arrow keys move focus, Enter/Space selects.
   */
  function handleBundleSwatchKeyboard(e) {
    const el = document.activeElement
    if (!el || el.type !== 'radio') return

    const card = el.closest('.bundle-card')
    if (!card) return

    const group = el.closest('.bundle-options-wrapper') || el.closest('.product-form__controls-group')
    if (!group) return

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      el.checked = true
      el.dispatchEvent(new Event('change', { bubbles: true }))
      return
    }

    const radios = Array.from(group.querySelectorAll('input[type="radio"]'))
    const idx = radios.indexOf(el)

    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault()
      if (idx < radios.length - 1) {
        el.setAttribute('tabindex', '-1')
        radios[idx + 1].setAttribute('tabindex', '0')
        radios[idx + 1].focus()
      }
    }

    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault()
      if (idx > 0) {
        el.setAttribute('tabindex', '-1')
        radios[idx - 1].setAttribute('tabindex', '0')
        radios[idx - 1].focus()
      }
    }
  }

  /**
   * Handle click events on bundle cards (ADD button, +/- buttons)
   */
  function handleBundleCardClick(e) {
    const target = e.target

    // ADD button
    if (target.closest('.bundle-card__add-btn')) {
      e.preventDefault()
      e.stopPropagation()
      const btn = target.closest('.bundle-card__add-btn')
      const card = btn.closest('.bundle-card')
      if (!card) return

      const variantId = card.dataset.variantId
      const price = card.dataset.price
      const compareAtPrice = card.dataset.compareAtPrice || price

      dispatchCartUpdateEvent(card, 1)

      if (window.BundleBuilder && window.BundleBuilder.updateBundleCardActions) {
        window.BundleBuilder.updateBundleCardActions(card, 1, price, variantId, compareAtPrice)
      }
      return
    }

    // Increase button
    if (target.closest('.bundle-card__qty-increase')) {
      e.preventDefault()
      e.stopPropagation()
      const btn = target.closest('.bundle-card__qty-increase')
      const card = btn.closest('.bundle-card')
      if (!card) return

      const qtyInput = card.querySelector('.bundle-card__qty-input')
      if (!qtyInput) return

      let currentQty = parseInt(qtyInput.value) || 0
      const maxQty = parseInt(qtyInput.max) || 999

      if (currentQty < maxQty) {
        currentQty++
        qtyInput.value = currentQty
        dispatchCartUpdateEvent(card, currentQty)
      }
      return
    }

    // Decrease button
    if (target.closest('.bundle-card__qty-decrease')) {
      e.preventDefault()
      e.stopPropagation()
      const btn = target.closest('.bundle-card__qty-decrease')
      const card = btn.closest('.bundle-card')
      if (!card) return

      const qtyInput = card.querySelector('.bundle-card__qty-input')
      if (!qtyInput) return

      let currentQty = parseInt(qtyInput.value) || 0

      if (currentQty > 0) {
        currentQty--
        qtyInput.value = currentQty
        dispatchCartUpdateEvent(card, currentQty)

        // If quantity goes to 0, switch back to ADD button
        if (currentQty === 0) {
          const variantId = card.dataset.variantId
          const price = card.dataset.price
          const compareAtPrice = card.dataset.compareAtPrice || price
          if (window.BundleBuilder && window.BundleBuilder.updateBundleCardActions) {
            window.BundleBuilder.updateBundleCardActions(card, 0, price, variantId, compareAtPrice)
          }
        }
      }
      return
    }
  }

  /**
   * Handle change events on bundle cards (color swatches, qty input)
   */
  function handleBundleCardChange(e) {
    const target = e.target

    // Color swatch change
    if (target.matches('.bundle-card .js-color-swatches input[type="radio"]')) {
      const card = target.closest('.bundle-card')
      if (!card || !target.checked) return

      const productHandle = card.dataset.product
      const currentColor = target.value
      const currentVariantId = target.dataset.variantId
      const variantImageUrl = target.dataset.variantImage

      // Quick image switch
      const primaryImage = card.querySelector('.media--first')
      if (variantImageUrl && primaryImage && variantImageUrl !== primaryImage.src) {
        const newImage = new Image()
        newImage.className = primaryImage.className
        newImage.alt = primaryImage.alt
        newImage.src = variantImageUrl
        newImage.onload = () => {
          primaryImage.parentNode.replaceChild(newImage, primaryImage)
        }
      }

      // Fetch full product data
      fetch(`${window.Shopify.routes.root}products/${productHandle}.js`)
        .then((response) => response.json())
        .then((product) => {
          const variant = product.variants.find(
            (item) =>
              (currentVariantId && item.id == currentVariantId) ||
              (!currentVariantId && item.options.includes(currentColor))
          )

          if (variant) {
            updateCardVariantData(card, variant)
            updateAddToBagButton(card, variant)

            if (window.BundleBuilder && window.BundleBuilder.syncQuantitySelectorWithState) {
              window.BundleBuilder.syncQuantitySelectorWithState(String(variant.id), card)
            }

            // Update image with transition (non-critical, wrapped in try-catch)
            try {
              const currentImage = card.querySelector('.media--first')
              if (variant.featured_media && variant.featured_media.src && currentImage) {
                const newPrimaryImage = createImageElement(
                  variant.featured_media,
                  currentImage.className,
                  currentImage.sizes,
                  product.title
                )

                if (newPrimaryImage.src && newPrimaryImage.src !== currentImage.src) {
                  currentImage.style.transition = 'opacity 200ms ease-in-out'
                  currentImage.style.opacity = '0'
                  setTimeout(() => {
                    currentImage.replaceWith(newPrimaryImage)
                    newPrimaryImage.style.opacity = '0'
                    newPrimaryImage.style.transition = 'opacity 200ms ease-in-out'
                    newPrimaryImage.offsetHeight
                    newPrimaryImage.style.opacity = '1'
                  }, 200)
                }
              }
            } catch (imgError) {
              console.warn('Image update failed:', imgError)
            }
          }
        })
        .catch((error) => {
          console.error('Error fetching product data:', error)
        })
      return
    }

    // Quantity input direct change
    if (target.matches('.bundle-card__qty-input')) {
      const card = target.closest('.bundle-card')
      if (!card) return

      let newQty = parseInt(target.value) || 0
      const minQty = parseInt(target.min) || 0
      const maxQty = parseInt(target.max) || 999

      if (newQty < minQty) newQty = minQty
      if (newQty > maxQty) newQty = maxQty

      target.value = newQty
      dispatchCartUpdateEvent(card, newQty)

      const variantId = card.dataset.variantId
      const price = card.dataset.price
      const compareAtPrice = card.dataset.compareAtPrice || price
      if (window.BundleBuilder && window.BundleBuilder.updateBundleCardActions) {
        window.BundleBuilder.updateBundleCardActions(card, newQty, price, variantId, compareAtPrice)
      }
      return
    }
  }

  /**
   * Resolve the right price for a variant inside a bundle card. Prefers
   * Shopify's market-aware price (so it matches sitewide exactly — including
   * manual per-market amounts) and falls back to legacy plan-adjusted base
   * cents if the market data isn't loaded.
   */
  function resolveCardPrice(variant) {
    if (window.BundleBuilder && typeof window.BundleBuilder.getCardDisplayPrice === 'function') {
      return window.BundleBuilder.getCardDisplayPrice(
        String(variant.id),
        variant.price,
        variant.compare_at_price
      )
    }

    let price = variant.price
    let compareAtPrice = variant.compare_at_price || variant.price
    if (window.BundleBuilder && window.BundleBuilder.getAdjustedPrice) {
      const adjusted = window.BundleBuilder.getAdjustedPrice(String(variant.id), variant.price)
      price = adjusted.price
      compareAtPrice = adjusted.compareAtPrice || compareAtPrice
    }
    return { price, compareAtPrice, priceFormatted: '', compareAtFormatted: '' }
  }

  /**
   * Update card data attributes with variant info
   */
  function updateCardVariantData(card, variant) {
    const { price, compareAtPrice } = resolveCardPrice(variant)

    card.dataset.variantId = variant.id
    card.dataset.price = price
    card.dataset.compareAtPrice = compareAtPrice
    card.dataset.variantTitle = variant.title
    if (variant.featured_media) {
      card.dataset.variantImage = variant.featured_media.src
    }
  }

  /**
   * Update price display and data attributes for new variant
   * Note: Button state is handled by syncQuantitySelectorWithState which checks cart
   */
  function updateAddToBagButton(card, variant) {
    const priceElement = card.querySelector('.card__title.price')

    const { price, compareAtPrice, priceFormatted } = resolveCardPrice(variant)

    card.dataset.variantId = variant.id
    card.dataset.price = price
    card.dataset.compareAtPrice = compareAtPrice
    card.dataset.variantTitle = variant.title

    if (priceElement) {
      // Prefer Shopify's Liquid-rendered money string so this matches the
      // product page / collection page exactly for manual per-market pricing.
      priceElement.textContent = priceFormatted || formatMoney(price)
    }
    // Button/quantity state is synced by syncQuantitySelectorWithState after this
  }

  /**
   * Dispatch cart update event
   */
  function dispatchCartUpdateEvent(card, quantity) {
    const event = new CustomEvent('bundle:cartUpdate', {
      bubbles: true,
      detail: {
        variantId: card.dataset.variantId,
        quantity: quantity,
        productHandle: card.dataset.productHandle || '',
        cardElement: card,
      },
    })
    card.dispatchEvent(event)
  }

  /**
   * Create image element from Shopify media
   */
  function createImageElement(media, className, sizes, altText) {
    const img = document.createElement('img')
    img.className = className
    img.alt = altText || ''
    img.sizes = sizes || '100vw'
    img.loading = 'lazy'

    // Guard against missing src
    if (!media || !media.src) {
      return img
    }

    const widths = [360, 535, 720, 940, 1066, 1200, 1400, 1600]
    const srcset = widths
      .map((width) => {
        const url = media.src.replace(/\.jpg|\.png|\.gif/, `_${width}x$&`)
        return `${url} ${width}w`
      })
      .join(', ')

    img.srcset = srcset
    img.src = media.src
    return img
  }

  /**
   * Fallback formatter for bundle card price updates — only used when no
   * pre-formatted market price string is available from
   * `BundleBuilder.getCardDisplayPrice`. In that happy path we render
   * Shopify's own Liquid-rendered money string directly, which is the
   * single source of truth for per-market pricing (including manual values
   * like a clean €19.90 that Shopify.currency.rate can't reproduce from
   * the USD base).
   *
   * Inputs here are expected to already be in **market-currency cents** —
   * `resolveCardPrice()` returns market cents via `getCardDisplayPrice`, and
   * the legacy getAdjustedPrice fallback only fires when the market map is
   * empty, in which case base ≈ market (same-currency market) and the
   * numbers still line up.
   */
  const ZERO_DECIMAL_CURRENCIES =
    (typeof window !== 'undefined' && window.SACHEU_FORMAT_MONEY_ZERO_DECIMAL_CURRENCIES) || [
      'BIF', 'CLP', 'DJF', 'GNF', 'ISK', 'JPY', 'KMF', 'KRW', 'MGA',
      'PYG', 'RWF', 'UGX', 'VND', 'VUV', 'XAF', 'XOF', 'XPF',
    ]

  /**
   * Whether the printed symbol should be separated from the number. Do not use
   * `symbol.length` (that wrongly adds a gap for "HK$", "C$", "A$", etc.).
   */
  const CURRENCY_SYMBOL_NEEDS_SPACE_AFTER = new Set([
    'CHF',
    'kr',
    'kr.',
    'zł',
    'Kč',
    'RM',
  ])

  function currencySymbolRequiresSpaceAfter(symbol, currencyCode) {
    if (!symbol) return false
    if (CURRENCY_SYMBOL_NEEDS_SPACE_AFTER.has(String(symbol).trim())) return true
    // Three-letter code used as the whole prefix (e.g. "DKK 250") — not compact glyph + prefix.
    if (currencyCode && String(symbol) === String(currencyCode) && /^[A-Z]{3}$/.test(currencyCode)) {
      return true
    }
    return false
  }

  function formatMoney(cents) {
    const marketCents = typeof cents === 'number' ? cents : parseInt(cents, 10) || 0

    const activeCurrencyCode =
      (typeof window !== 'undefined' && window.theme && window.theme.currencyCode) ||
      (typeof window !== 'undefined' && window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
      'USD'

    // Prefer the market's configured symbol (cart.currency.symbol, exposed
    // via window.theme) so the bundle cards match the rest of the site.
    // Intl renders DKK as "DKK 250" while Shopify uses "kr. 250"; always
    // defer to Shopify's symbol.
    const currencySymbol =
      (typeof window !== 'undefined' && window.theme && window.theme.currencySymbol) ||
      (activeCurrencyCode === 'USD' ? '$' : activeCurrencyCode)

    const isZeroDecimal = ZERO_DECIMAL_CURRENCIES.indexOf(activeCurrencyCode) !== -1
    const amount = isZeroDecimal ? marketCents : marketCents / 100
    // Drop the decimal portion when the price is a whole currency unit so
    // "$12.00" renders as "$12" — mirrors the `format-money` Liquid snippet.
    const hasFractional = !isZeroDecimal && marketCents % 100 !== 0

    // Pin to 'en-US' so the decimal separator is always "." and the
    // thousands grouping is always "," — matches the server-side
    // `format-money` snippet across every market.
    let digits
    try {
      digits = amount.toLocaleString('en-US', {
        minimumFractionDigits: hasFractional ? 2 : 0,
        maximumFractionDigits: hasFractional ? 2 : 0,
      })
    } catch (e) {
      digits = hasFractional ? amount.toFixed(2) : String(Math.round(amount))
    }

    const separator = currencySymbolRequiresSpaceAfter(currencySymbol, activeCurrencyCode) ? ' ' : ''
    return currencySymbol + separator + digits
  }

  /**
   * Set max quantity for all cards
   */
  window.setBundleCardMaxQuantity = function (maxQty) {
    document.querySelectorAll('.bundle-card__qty-input').forEach((input) => {
      input.max = maxQty
    })
  }

  /**
   * Reset a card quantity to 0
   */
  window.resetBundleCard = function (variantId) {
    const card = document.querySelector(`.bundle-card[data-variant-id="${variantId}"]`)
    if (!card) return
    const qtyInput = card.querySelector('.bundle-card__qty-input')
    if (qtyInput) {
      qtyInput.value = '0'
    }
  }
})()
